// The PIPELINE's data layer (spec 09 §2.24, §2.25, §3.11, §4.14 and §5's E2-1
// rows; D-312): the stages, the board, the deal list, one deal, the forecast,
// and the seven deal writes (create, edit, move, win, lose, reopen, and the
// quote link). The Mark won and Link a quote pickers read no deal list: the
// quote and order list rows carry the facts they judge by (#293), so a win,
// a reopen and a link also read those lists again. Same posture as the other sales query files: the generated
// api-client only (doctrine 3), `{signal}` threaded through every read, and
// every mutation invalidates exactly the reads it can have moved.
//
// ONE PREFIX FOR EVERY DEAL READ. The board, the list, a deal and the forecast
// all sit under `["sales", "opportunities"]`, so a write invalidates the one
// prefix and whichever of the four is mounted refetches. A deal write can move
// any of them: a move changes a column's count and total, the list's stage
// cell, the deal page's header and nothing in the forecast, but a win or a
// loss moves the forecast too, and keying each invalidation to its exact
// reads would be one more thing to get wrong for no request saved.
//
// A DEAL WRITE SETTLES AFTER THE DEAL READS HAVE READ AGAIN. `onSettled`
// returns the invalidation, and TanStack Query awaits it before the write's
// promise settles. The board clears its pending move when that promise
// settles and then paints the columns as cached, so a promise that settled
// first painted the card back in the column it left for one round trip (and a
// second drag in that window named the stage the card had already left).
//
// A DEAL WRITE DROPS THE FORECAST AND THE BOARD NOBODY IS LOOKING AT. An
// inactive query is only marked stale by an invalidation, and a remount paints
// its cached figures before the refetch replaces them, so a reader who won a
// deal and then opened the forecast saw last hour's Won and Pipeline cells move
// under them (design-system §5). Removing the inactive entries makes the next
// mount start from the skeleton; the mounted ones refetch in place.
//
// THE MOVE CARRIES ITS OWN IDEMPOTENCY KEY. Every other write mints a fresh
// key per press, as every sales write does. A board drop that did not reach
// the server is retried with Try again, and the retry must be the SAME
// request, so the caller mints the key once per drop and passes it in
// (spec 09 §4.14; plan §7's "Try again reusing the same Idempotency-Key"). A
// replay of a move that did land answers the stored 200, never a second move.
//
// WHAT A REFUSAL IS. `unwrap` turns an HTTP refusal into a `RequestError`
// carrying the problem's `code`, and a request that never reached the server
// rejects with the fetch's own `TypeError`. The board tells those two apart
// (`isNetworkFailure` below), because a refused drop names its reason and a
// lost one offers Try again.

import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { UnauthorizedError } from "../../../../lib/problem";
import { newIdempotencyKey, RequestError, unwrap } from "../../../../lib/request";
import { activityKeys } from "./activity-queries";
import { quoteKeys } from "./quote-queries";
import { salesKeys } from "./sales-queries";
import { keepPageOnTurn } from "../../../../lib/cursor-pages";

type S = components["schemas"];

export type Opportunity = S["Opportunity"];
export type OpportunityDetail = S["OpportunityDetail"];
export type OpportunityStatus = S["OpportunityStatus"];
export type OpportunityLinkedQuote = S["OpportunityLinkedQuote"];
export type PipelineStage = S["PipelineStage"];
export type OpportunityBoard = S["OpportunityBoardResponse"];
export type OpportunityBoardColumn = S["OpportunityBoardColumn"];
export type OpportunityBoardValues = S["OpportunityBoardValues"];
export type OpportunityForecast = S["OpportunityForecastResponse"];
export type OpportunityForecastRow = S["OpportunityForecastRow"];
export type OpportunityForecastSlipped = S["OpportunityForecastSlipped"];
export type OpportunityCreateRequest = S["OpportunityCreateRequest"];
export type OpportunityUpdateRequest = S["OpportunityUpdateRequest"];
export type OpportunityMoveRequest = S["OpportunityMoveRequest"];
export type OpportunityWinRequest = S["OpportunityWinRequest"];
export type OpportunityLoseRequest = S["OpportunityLoseRequest"];
export type PipelineStageUpdateRequest = S["PipelineStageUpdateRequest"];
export type SalesNextActivity = S["SalesNextActivity"];
/** §2.12's four reason codes, which a lost deal shares with a rejected quote. */
export type LostReason = S["QuoteRejectionReason"];

export type OpportunitiesQuery = NonNullable<
  paths["/v1/opportunities"]["get"]["parameters"]["query"]
>;
export type OpportunityBoardQuery = NonNullable<
  paths["/v1/opportunities/board"]["get"]["parameters"]["query"]
>;
export type OpportunityForecastQuery = NonNullable<
  paths["/v1/opportunities/forecast"]["get"]["parameters"]["query"]
>;

/** A board column reads at most this many cards, and Show more asks for the
 * same number again (spec 09 §5's board row). */
export const BOARD_PAGE_SIZE = 25;

export const opportunityKeys = {
  /** Every deal read shares this prefix (the header says why). */
  all: () => ["sales", "opportunities"] as const,
  list: (query: OpportunitiesQuery, cursor: string | undefined) =>
    ["sales", "opportunities", "list", query, cursor ?? null] as const,
  board: (query: OpportunityBoardQuery) => ["sales", "opportunities", "board", query] as const,
  forecast: (query: OpportunityForecastQuery) =>
    ["sales", "opportunities", "forecast", query] as const,
  detail: (id: string) => ["sales", "opportunities", "detail", id] as const,
  stages: () => ["sales", "pipeline-stages"] as const,
};

/** Did this request fail to reach the server at all? A refusal is a
 * `RequestError` and a lost session an `UnauthorizedError`; anything else a
 * write rejects with is the fetch failing (offline, DNS, a dropped socket). */
export function isNetworkFailure(error: unknown): boolean {
  return (
    error !== undefined &&
    error !== null &&
    !(error instanceof RequestError) &&
    !(error instanceof UnauthorizedError)
  );
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** `GET /v1/pipeline-stages`: the tenant's stages by position. `enabled`
 * carries the screen's lineage gate, so a reader the mirror knows is refused
 * sends nothing (spec 09 §4.13). */
export function usePipelineStages(enabled = true) {
  return useQuery({
    queryKey: opportunityKeys.stages(),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/pipeline-stages", { signal });
      return unwrap(res, "Could not load the pipeline stages");
    },
  });
}

/** `GET /v1/opportunities/board`: every column at once. */
export function useOpportunityBoard(query: OpportunityBoardQuery, enabled = true) {
  return useQuery({
    queryKey: opportunityKeys.board(query),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/opportunities/board", {
        signal,
        params: { query },
      });
      return unwrap(res, "Could not load the board");
    },
  });
}

/**
 * ONE PAGE of `GET /v1/opportunities`, as a plain function, so the list's
 * hook, the board's Show more and the CSV export walk the same request
 * (D-289 ruling 1's rule for the books).
 */
export async function fetchOpportunitiesPage(
  query: OpportunitiesQuery,
  cursor: string | undefined,
  signal?: AbortSignal,
) {
  const res = await client.GET("/v1/opportunities", {
    ...(signal !== undefined ? { signal } : {}),
    params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
  });
  return unwrap(res, "Could not load the deals");
}

export function useOpportunities(
  query: OpportunitiesQuery,
  cursor: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: opportunityKeys.list(query, cursor),
    enabled,
    // A page turn keeps the page shown until the next lands (D-560).
    placeholderData: keepPageOnTurn(opportunityKeys.list(query, cursor)),
    queryFn: async ({ signal }) => fetchOpportunitiesPage(query, cursor, signal),
  });
}

/** Every page a picker's walk holds, one live query per cursor under the key
 * `useOpportunities` reads (the composer's Link to a deal, #293), so a deal
 * write's invalidation reads every walked page again. */
export function useOpportunityPages(
  query: OpportunitiesQuery,
  cursors: readonly (string | undefined)[],
) {
  return useQueries({
    queries: cursors.map((cursor) => ({
      queryKey: opportunityKeys.list(query, cursor),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchOpportunitiesPage(query, cursor, signal),
    })),
  });
}

/** `GET /v1/opportunities/{id}`: the deal page's read, and the deal peek's. */
export function useOpportunity(id: string, enabled = true) {
  return useQuery({
    queryKey: opportunityKeys.detail(id),
    enabled: enabled && id.length > 0,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/opportunities/{id}", {
        signal,
        params: { path: { id } },
      });
      return unwrap(res, "Could not load the deal");
    },
  });
}

/** `GET /v1/opportunities/forecast`, a value door. `enabled` carries the
 * screen's verdict, so a reader the mirror knows is refused sends nothing. */
export function useOpportunityForecast(query: OpportunityForecastQuery, enabled = true) {
  return useQuery({
    queryKey: opportunityKeys.forecast(query),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/opportunities/forecast", {
        signal,
        params: { query },
      });
      return unwrap(res, "Could not load the forecast");
    },
  });
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** What a deal write can have moved besides the deal reads: the customer book
 * and the 360 (an open deal is live business, §3.1 item 4, and the discipline
 * flag reads it), and the task list, whose rows print the deal's number and
 * title. The returned promise settles when the mounted deal reads have read
 * again (the header); the other three stay fire-and-forget. */
function invalidateDealReads(qc: ReturnType<typeof useQueryClient>): Promise<void> {
  qc.removeQueries({ queryKey: [...opportunityKeys.all(), "forecast"], type: "inactive" });
  qc.removeQueries({ queryKey: [...opportunityKeys.all(), "board"], type: "inactive" });
  void qc.invalidateQueries({ queryKey: salesKeys.customerLists() });
  void qc.invalidateQueries({ queryKey: ["sales", "customer"] });
  void qc.invalidateQueries({ queryKey: activityKeys.all() });
  return qc.invalidateQueries({ queryKey: opportunityKeys.all() });
}

/** `POST /v1/opportunities`. The 201 is the new deal. */
export function useCreateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: OpportunityCreateRequest) => {
      const res = await client.POST("/v1/opportunities", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not create the deal");
    },
    onSuccess: () => {
      // A create naming `quote_id` links that quote.
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
      return invalidateDealReads(qc);
    },
  });
}

/** `PATCH /v1/opportunities/{id}`. */
export function useUpdateOpportunity(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: OpportunityUpdateRequest) => {
      const res = await client.PATCH("/v1/opportunities/{id}", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not save the deal");
    },
    onSuccess: () => invalidateDealReads(qc),
  });
}

export interface MoveVariables {
  readonly id: string;
  readonly body: OpportunityMoveRequest;
  /** Minted once per drop, so Try again replays the same request. */
  readonly idempotencyKey: string;
}

/** `POST /v1/opportunities/{id}/move`. Settles by refetching the deal reads,
 * refused or not: a refused drop's card must end where the deal really is. */
export function useMoveOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body, idempotencyKey }: MoveVariables) => {
      const res = await client.POST("/v1/opportunities/{id}/move", {
        params: { path: { id }, header: { "Idempotency-Key": idempotencyKey } },
        body,
      });
      return unwrap(res, "Could not move the deal");
    },
    onSettled: () => invalidateDealReads(qc),
  });
}

export interface WinVariables {
  readonly id: string;
  readonly body: OpportunityWinRequest;
  readonly idempotencyKey?: string | undefined;
}

/** `POST /v1/opportunities/{id}/win`. */
export function useWinOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body, idempotencyKey }: WinVariables) => {
      const res = await client.POST("/v1/opportunities/{id}/win", {
        params: {
          path: { id },
          header: { "Idempotency-Key": idempotencyKey ?? newIdempotencyKey() },
        },
        body,
      });
      return unwrap(res, "Could not mark the deal won");
    },
    onSettled: () => {
      // A win naming an unlinked quote links it, and the order it names now
      // backs a won deal: both lists' rows carry those facts (#293).
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
      void qc.invalidateQueries({ queryKey: ["sales", "quote"] });
      return invalidateDealReads(qc);
    },
  });
}

export interface LoseVariables {
  readonly id: string;
  readonly body: OpportunityLoseRequest;
  readonly idempotencyKey?: string | undefined;
}

/** `POST /v1/opportunities/{id}/lose`. */
export function useLoseOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body, idempotencyKey }: LoseVariables) => {
      const res = await client.POST("/v1/opportunities/{id}/lose", {
        params: {
          path: { id },
          header: { "Idempotency-Key": idempotencyKey ?? newIdempotencyKey() },
        },
        body,
      });
      return unwrap(res, "Could not mark the deal lost");
    },
    onSettled: () => invalidateDealReads(qc),
  });
}

/** `POST /v1/opportunities/{id}/reopen`. No body. */
export function useReopenOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/opportunities/{id}/reopen", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not reopen the deal");
    },
    onSettled: () => {
      // A reopen clears the win stamps, so its order backs no deal any more
      // (the order list row's `backs_won_opportunity`, #293).
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
      return invalidateDealReads(qc);
    },
  });
}

export interface LinkVariables {
  readonly quoteId: string;
  /** The deal to link, or `null` to unlink. */
  readonly opportunityId: string | null;
}

/** `POST /v1/quotes/{id}/opportunity`: link a quote to a deal, or unlink it. */
export function useLinkQuoteOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ quoteId, opportunityId }: LinkVariables) => {
      const res = await client.POST("/v1/quotes/{id}/opportunity", {
        params: { path: { id: quoteId }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { opportunity_id: opportunityId },
      });
      return unwrap(res, "Could not change the quote's deal");
    },
    onSettled: (_data, _error, variables) => {
      void qc.invalidateQueries({ queryKey: opportunityKeys.all() });
      void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
      // An order row names its quote's deal (#293).
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
      void qc.invalidateQueries({ queryKey: quoteKeys.quote(variables.quoteId) });
    },
  });
}

/** `PATCH /v1/pipeline-stages/{id}`: the settings card's two numbers. */
export function useUpdatePipelineStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PipelineStageUpdateRequest }) => {
      const res = await client.PATCH("/v1/pipeline-stages/{id}", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not save the stage");
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: opportunityKeys.stages() });
      // Probability is live: every open deal's weighted value moves with it.
      void qc.invalidateQueries({ queryKey: opportunityKeys.all() });
    },
  });
}
